// components/ControlPanel.tsx
import React, { useEffect, useRef, useState } from "react";
import LongPressButton from "./LongPress";
import MieGraph from "./MieGraphScreen";
import { useBLE } from "../../../hooks/useBle";
import {
  IS_WEB_BUILD,
  WEB_REMOTE_GRAPH_MIN_HEIGHT,
} from "../../../utils/constants";

interface ControlPanelProps {
  isAuto: boolean;
  isShortHeight: boolean;
  pauseTime: number;
  onPauseTimeChange?: (newTime: number) => void;
  isMobile: boolean;
}

const ControlPanel: React.FC<ControlPanelProps> = ({
  isAuto,
  isShortHeight,
  isMobile,
}) => {
  const [availableHeight, setAvailableHeight] = useState<number>(500);
  const [availableWidth, setAvailableWidth] = useState<number>(400);
  const [dimensionsReady, setDimensionsReady] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    handleInspPhaseStart,
    handleInspPhaseStop,
    handleExpPhaseStart,
    handleExpPhaseStop,
  } = useBLE();

  useEffect(() => {
    const calculateAvailableDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();

        // Calculate available width
        const calculatedWidth = rect.width || containerRef.current.offsetWidth;

        let calculatedHeight: number;
        if (IS_WEB_BUILD && isAuto) {
          // Web: CSS min-height on the parent gives the true height.
          calculatedHeight = rect.height;
        } else if (IS_WEB_BUILD) {
          // Web/Manual: play button is in flow too, don't subtract it.
          const bottomPadding = 40;
          calculatedHeight = window.innerHeight - (rect.top + bottomPadding);
        } else {
          // Native: unchanged.
          const playButton = document.querySelector(".play-button-container");
          const bottomPadding = 40; // set same as bottom value of play pause button
          calculatedHeight =
            window.innerHeight -
            (rect.top +
              bottomPadding +
              (playButton ? playButton.clientHeight : 0));
        }

        // Set minimum dimensions to prevent cramping.
        const minHeight =
          IS_WEB_BUILD && isAuto
            ? isShortHeight
              ? WEB_REMOTE_GRAPH_MIN_HEIGHT.short
              : WEB_REMOTE_GRAPH_MIN_HEIGHT.normal
            : 10;
        const minWidth = 300;

        const finalHeight = Math.max(calculatedHeight, minHeight);
        const finalWidth = Math.max(calculatedWidth, minWidth);

        // console.log("Final Height:", finalHeight);
        // console.log("Final Width:", finalWidth);

        setAvailableHeight(finalHeight);
        setAvailableWidth(finalWidth);

        // Web/Auto: always >= the floor (Math.max above), so ready
        // as soon as we've measured once.
        const isReady =
          IS_WEB_BUILD && isAuto
            ? true
            : finalHeight > minHeight && finalWidth > minWidth;
        if (isReady) {
          setDimensionsReady(true);
        }
      }
    };

    // Reset dimensions ready state
    setDimensionsReady(false);

    // Initial calculation with a slight delay to ensure DOM is ready
    const initialTimer = setTimeout(() => {
      calculateAvailableDimensions();
    }, 100);

    // Recalculate on window resize and orientation change
    const handleResize = () => {
      setDimensionsReady(false);
      setTimeout(() => {
        calculateAvailableDimensions();
      }, 100);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    // Cleanup
    return () => {
      clearTimeout(initialTimer);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, [isShortHeight, isMobile, isAuto]);

  // Recalculate when layout affecting props change

  const effectiveButtonContainerHeight = React.useMemo(() => {
    if (typeof window !== "undefined") {
      const w = window.innerWidth;
      const h = window.innerHeight;
      // Very small devices
      if (w <= 360 || h <= 600) {
        return Math.min(availableHeight, 180);
      }
      // Small phones / compact heights
      if (w <= 400 || h <= 700) {
        return Math.min(availableHeight, 220);
      }
      // General mobile or short-height layout
      if (isMobile || isShortHeight) {
        return Math.min(availableHeight, 240);
      }
      // Desktop / tablet landscape: align with LongPress desktop scaling
      if (w >= 1024) {
        return Math.min(availableHeight, 260);
      }
    }
    return availableHeight;
  }, [availableHeight, isMobile, isShortHeight]);

  // Web: unset height so it stretches to fill the flex parent.
  let containerHeight: string | undefined = "auto";
  if (isAuto) {
    containerHeight = IS_WEB_BUILD ? undefined : `${availableHeight}px`;
  }

  return (
    <div
      style={{
        height: containerHeight,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
      ref={containerRef}
    >
      {isAuto ? (
        dimensionsReady ? (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <MieGraph width={availableWidth} height={availableHeight} />
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              color: "#666",
            }}
          >
            Loading graph...
          </div>
        )
      ) : (
        <div className="flex gap-4">
          <LongPressButton
            isShortHeight={isShortHeight}
            onTouchStart={async () => await handleInspPhaseStart()}
            onTouchEnd={async () => await handleInspPhaseStop()}
            iconType="plus"
            ariaLabel="Increase pause time"
            color="#D9D9D9"
            containerHeight={effectiveButtonContainerHeight}
          />
          <LongPressButton
            isShortHeight={isShortHeight}
            onTouchStart={async () => await handleExpPhaseStart()}
            onTouchEnd={async () => await handleExpPhaseStop()}
            iconType="minus"
            ariaLabel="Decrease pause time"
            color="#D9D9D9"
            containerHeight={effectiveButtonContainerHeight}
          />
        </div>
      )}
    </div>
  );
};

export default ControlPanel;
